import {
  FloatType,
  NearestFilter,
  OrthographicCamera,
  Points,
  RGBAFormat,
  Scene,
  ShaderMaterial,
  Vector3,
  WebGLRenderTarget,
  type Object3D,
  type WebGLRenderer,
} from "three";
import { fitSurfaceSample, type SurfaceSample } from "../functions/surface";

/**
 * Render-based surface sampler (ported from the poc/editor gpuSurface.ts),
 * refactored from a class to a factory closure to follow the kho taxonomy: an
 * instantiable GPU resource is a factory function, not a class. The PURE math
 * (median/MAD filter + covariance/eigen → normal + centroid) lives in
 * editor/functions/surface.ts; this closure owns only the GPU state.
 *
 * For a pick at `anchor` with radius R: an ortho camera looks along the view ray
 * at the R-neighborhood, renders the point-cloud tiles into a small float target
 * (nearest-per-pixel via depth test), then fitSurfaceSample() rejects noise and
 * PCA-fits the survivors → surface normal + centroid. This is what makes a placed
 * object "stick" to the point cloud (spec: 点群に擦りつく) instead of the IFC mesh.
 *
 * Adapted for the sc-quick3d client: instead of one CloudData geometry it samples
 * every THREE.Points found in the given point-cloud scenes (streamed octree
 * tiles), via lightweight proxies that share the tile GPU geometry.
 */
const SIZE = 32;

export type { SurfaceSample } from "../functions/surface";

export interface SurfaceSampler {
  /**
   * @param anchor  coarse hit point under the mouse
   * @param viewDir normalized ray direction (camera → scene)
   * @param radius  sampling radius R (world units)
   * @param scenes  point-cloud scenes containing the tile THREE.Points
   * @param avgSpacing average point spacing (world units) for the splat size
   */
  sample(
    anchor: Vector3,
    viewDir: Vector3,
    radius: number,
    scenes: Object3D[],
    avgSpacing?: number,
    opts?: { backoff?: number; farMul?: number },
  ): SurfaceSample | null;
  dispose(): void;
}

export function createSurfaceSampler(renderer: WebGLRenderer): SurfaceSampler {
  const target = new WebGLRenderTarget(SIZE, SIZE, {
    type: FloatType,
    format: RGBAFormat,
    minFilter: NearestFilter,
    magFilter: NearestFilter,
    depthBuffer: true,
  });
  const scene = new Scene();
  const camera = new OrthographicCamera();
  const buf = new Float32Array(SIZE * SIZE * 4);
  let proxies: Points[] = [];
  const material = new ShaderMaterial({
    uniforms: {
      uAnchor: { value: new Vector3() },
      uInvR: { value: 1 },
      uPointPx: { value: 2 },
    },
    vertexShader: /* glsl */ `
      uniform float uPointPx;
      uniform vec3 uAnchor;
      uniform float uInvR;
      varying vec3 vRel;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vRel = (wp.xyz - uAnchor) * uInvR;
        gl_Position = projectionMatrix * viewMatrix * wp;
        gl_PointSize = uPointPx;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vRel;
      void main() { gl_FragColor = vec4(vRel, 1.0); }
    `,
  });

  let lastKey = "";

  /**
   * Bind proxy Points (sharing tile GPU geometry) for every THREE.Points in the
   * scenes. Rebuilds ONLY when the tile set changes (keyed by uuid list); during
   * a drag the tiles are stable, so proxies are reused (no per-sample allocation
   * — that was the FPS killer vs the POC which binds the cloud once).
   */
  const syncProxies = (scenes: Object3D[]) => {
    const tiles: Points[] = [];
    for (const root of scenes) {
      root.updateMatrixWorld(true);
      root.traverse((obj) => {
        if ((obj as Points).isPoints && (obj as Points).geometry) {
          tiles.push(obj as Points);
        }
      });
    }
    const key = tiles.map((t) => t.uuid).join(",");
    if (key === lastKey && proxies.length === tiles.length) {
      // reuse existing proxies; just refresh world matrices (cheap)
      for (let i = 0; i < tiles.length; i++) {
        proxies[i].matrix.copy(tiles[i].matrixWorld);
      }
      return;
    }
    for (const p of proxies) scene.remove(p);
    proxies.length = 0;
    for (const t of tiles) {
      // the sampler camera is a tiny ortho box (±radius) around the cast point;
      // frustum culling drops the far tiles so only the 1-2 tiles under the
      // anchor get vertex-shaded — WITHOUT this the GPU processes every point of
      // every tile per sample (millions), which stalls readRenderTargetPixels on
      // the main thread and tanks FPS during a drag. Needs a bounding sphere.
      if (!t.geometry.boundingSphere) t.geometry.computeBoundingSphere();
      const proxy = new Points(t.geometry, material);
      proxy.matrixAutoUpdate = false;
      proxy.matrix.copy(t.matrixWorld);
      proxy.matrixWorldNeedsUpdate = true;
      proxy.frustumCulled = true;
      scene.add(proxy);
      proxies.push(proxy);
    }
    lastKey = key;
  };

  const sample: SurfaceSampler["sample"] = (
    anchor,
    viewDir,
    radius,
    scenes,
    avgSpacing = 0.5,
    opts = {},
  ) => {
    syncProxies(scenes);
    if (proxies.length === 0) return null;
    const backoff = opts.backoff ?? 4;
    const farMul = opts.farMul ?? 8;

    const cam = camera;
    cam.position.copy(anchor).addScaledVector(viewDir, -backoff * radius);
    const up = Math.abs(viewDir.y) > 0.95 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0);
    cam.up.copy(up);
    cam.lookAt(new Vector3().copy(anchor).add(viewDir));
    cam.left = -radius;
    cam.right = radius;
    cam.top = radius;
    cam.bottom = -radius;
    cam.near = 0.01 * radius;
    cam.far = (backoff + farMul) * radius;
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();

    const spacingPx = (SIZE * avgSpacing) / (2 * radius);
    material.uniforms.uPointPx.value = Math.min(8, Math.max(1.5, spacingPx * 1.5));
    material.uniforms.uAnchor.value.copy(anchor);
    material.uniforms.uInvR.value = 1 / radius;

    const prevTarget = renderer.getRenderTarget();
    const prevAlpha = renderer.getClearAlpha();
    renderer.setRenderTarget(target);
    renderer.setClearAlpha(0);
    renderer.clear();
    renderer.render(scene, cam);
    renderer.readRenderTargetPixels(target, 0, 0, SIZE, SIZE, buf);
    renderer.setRenderTarget(prevTarget);
    renderer.setClearAlpha(prevAlpha);

    return fitSurfaceSample(buf, SIZE * SIZE, viewDir, anchor, radius);
  };

  const dispose = () => {
    for (const p of proxies) scene.remove(p);
    proxies.length = 0;
    target.dispose();
    material.dispose();
  };

  return { sample, dispose };
}
